import { Validators, FormArray, FormControl, FormGroup } from '@angular/forms';
import { getActivity, getLablel, getOrg, getValue } from '../mappers';
import {
  IAutocompleteCtx,
  IInputCtx,
  IMultiTagsCtx,
  ISelectCtx,
  ITextCtx
} from '../../shared/form';
import { of } from 'rxjs';
import { AutoCompleteService } from '../../core/services/autocomplete.service';
import { filter } from 'rxjs/operators';
import { IOption } from '../organization/organization.service';

export class EducatorForm {
  // ---------------------------------------------
  //                Form
  // ---------------------------------------------
  readonly fields = {
    name: new FormControl(null, [Validators.required]),
    lastName: new FormControl(null, [Validators.required]),
    type: new FormControl(null, [Validators.required]),
    zip: new FormControl(null, [Validators.required]),
    country: new FormControl(null, [Validators.required]),
    city: new FormControl(null, [Validators.required]),
    address: new FormControl(null, [Validators.required]),
    currentschool: new FormControl(null, [Validators.required]),
    subjects: new FormArray([]),
    education: new FormArray([]),
    otherexperience: new FormArray([]),
    associations: new FormArray([]),
    honorsandavards: new FormArray([]),
    certifications: new FormArray([]),
    wants: new FormArray([]),
    haves: new FormArray([]),
    about_me: new FormControl()
  };

  readonly form = new FormGroup(this.fields);
  // ---------------------------------------------
  //                Inputs
  // ---------------------------------------------
  readonly firstName: IInputCtx = {
    control: this.fields.name,
    placeholder: `First Name`
  };

  readonly lastName: IInputCtx = {
    control: this.fields.lastName,
    placeholder: `Last Name`
  };

  readonly type: ISelectCtx<string> = {
    placeholder: `Educator Type`,
    options: of([`Teacher`, `Faculty`, `Tutor`, `Coach`, `Mentor`]),
    control: this.fields.type,
    getTitle: (value) => value
  };

  readonly country = this.auto.getCountry(this.fields.country);
  readonly city = this.auto.getCity(this.fields.city);

  readonly zip: IInputCtx = {
    control: this.fields.zip,
    placeholder: `Zip / Post Code`
  };

  readonly institution: IAutocompleteCtx<IOption> = {
    control: this.fields.currentschool,
    placeholder: `Current organization, company or institution`,
    getTitle: getValue,
    getOptionTitle: getLablel,
    options: this.auto.getOptions(this.fields.currentschool, `autocomplete/org`)
  };

  readonly address: IAutocompleteCtx<IOption> = {
    control: this.fields.address,
    placeholder: `Address`,
    getTitle: getValue,
    options: of([])
  };

  readonly interestsTags: IMultiTagsCtx<any> = {
    inputs: [
      this.auto.getAutocomplete(
        `Subjects / Expertise`,
        `get/field/specialization`,
        `Math Teacher or Physics Tutor or Tennis Coach`
      )
    ],
    getTitle: (interest) => `${interest}`,
    map: ([interest]) => getValue(interest),
    values: this.fields.subjects
  };

  readonly education: IMultiTagsCtx<any> = {
    inputs: [
      this.auto.getAutocomplete(`Education`, ``, `Degree in BA in Math`),
      this.auto.getAutocomplete(
        `Organization`,
        `autocomplete/org`,
        `Rushmore Academy`
      ),
      this.auto.getAutocomplete(`Address`, ``)
    ],
    map: getActivity,
    getTitle: (activity) => `${activity.name} @ ${activity.body.name}`,
    values: this.fields.education
  };

  readonly work: IMultiTagsCtx<any> = {
    inputs: [
      this.auto.getAutocomplete(
        `Work & other experience`,
        `get/field/otherexperience`,
        `Volunteer`
      ),
      this.auto.getAutocomplete(
        `Organization`,
        `autocomplete/org`,
        `NYC Public Library`
      ),
      this.auto.getAutocomplete(`Address`, ``)
    ],
    getTitle: (activity) => `${activity.name} @ ${activity.body.name}`,
    map: getActivity,
    values: this.fields.otherexperience
  };

  readonly honors: IMultiTagsCtx<any> = {
    inputs: [
      this.auto.getAutocomplete(
        `Honors & awards`,
        `get/field/honorsandavards`,
        `Third Prize`
      ),
      this.auto.getAutocomplete(
        `Contest or Event`,
        `autocomplete/org`,
        `Science Fair`
      ),
      this.auto.getAutocomplete(`Address`, ``)
    ],
    map: getActivity,
    getTitle: (activity) => `${activity.name} @ ${activity.body.name}`,
    values: this.fields.honorsandavards
  };

  readonly haves: IMultiTagsCtx<any> = {
    inputs: [
      this.auto.getAutocomplete(
        `Haves`,
        `get/field/have`,
        `Tutor with SAT/ACT track record`
      )
    ],
    map: ([value]) => getValue(value),
    getTitle: (value) => `${value}`,
    values: this.fields.haves
  };

  readonly wants: IMultiTagsCtx<any> = {
    inputs: [
      this.auto.getAutocomplete(
        `Wants`,
        `get/field/wants`,
        `Interested history tutoring opportunities`
      )
    ],
    map: ([value]) => getValue(value),
    getTitle: (value) => `${value}`,
    values: this.fields.wants
  };

  readonly associations: IMultiTagsCtx<any> = {
    inputs: [
      this.auto.getAutocomplete(
        `Associations`,
        `get/field/associations`,
        `PTA or National Math League`
      )
    ],
    map: ([value]) => getValue(value),
    getTitle: (value) => `${value}`,
    values: this.fields.associations
  };

  readonly certifications: IMultiTagsCtx<any> = {
    inputs: [
      this.auto.getAutocomplete(
        `Professional Certifications`,
        `get/field/certifications`,
        `Math Teacher Certification`
      )
    ],
    map: ([value]) => getValue(value),
    getTitle: (value) => `${value}`,
    values: this.fields.certifications
  };

  readonly aboutMe: ITextCtx = {
    control: this.fields.about_me,
    placeholder: `About me`,
    maxLength: 250
  };
  // ---------------------------------------------
  //                Init
  // ---------------------------------------------
  constructor(readonly auto: AutoCompleteService) {
    this.auto.lockOrg(this.fields.currentschool, this.fields.address, true);

    this.auto.lockOrg(this.institution.control, this.address.control);

    this.auto.lockOrg(
      this.education.inputs[1].control,
      this.education.inputs[2].control
    );

    this.auto.lockOrg(this.work.inputs[1].control, this.work.inputs[2].control);

    this.auto.lockOrg(
      this.honors.inputs[1].control,
      this.honors.inputs[2].control
    );
  }
  // ---------------------------------------------
  //                Api
  // ---------------------------------------------
  public getFormData() {
    const object = this.form.value;
    const mapped = {
      member: object.type,
      zip: object.zip,
      interests: object.subjects,
      evolutions: object.education,
      associations: object.associations,
      certifications: object.certifications,
      wants: object.wants,
      have: object.haves,
      currentschool: getOrg(object.currentschool, object.address),
      name: object.name,
      lastname: object.lastName,
      country: getValue(object.country),
      city: getValue(object.city),
      otherexperience: object.otherexperience,
      honorsandavards: object.honorsandavards,
      about_me: object.about_me
    };

    return mapped;
  }
}
